/**
 * Helpers pour le routing multilingue avec préfixe d'URL `/:lang/...`
 *
 * Stratégie SEO : chaque langue a sa propre URL indexable par Google.
 * - `/` ou `/fr/...` → français (langue par défaut)
 * - `/en/...` → anglais
 * - `/de/...` → allemand
 * - `/it/...` → italien
 * - `/es/...` → espagnol
 *
 * Les pages SEO-stratégiques (Landing, Blog) consomment ces préfixes.
 * Les pages internes (Dashboard, Admin) ignorent le préfixe.
 */

export const SUPPORTED_LANGUAGES = ['fr', 'en', 'de', 'it', 'es'];
export const DEFAULT_LANGUAGE = 'fr';

const PUBLIC_BASE_URL = 'https://drone-maps.eu';

/**
 * Extrait la langue d'un pathname (ex: "/de/blog/post-x" → "de").
 * Retourne null si aucune langue valide n'est détectée.
 */
export function extractLangFromPath(pathname) {
  const match = pathname.match(/^\/([a-z]{2})(?:\/|$)/i);
  if (!match) return null;
  const lang = match[1].toLowerCase();
  return SUPPORTED_LANGUAGES.includes(lang) ? lang : null;
}

/**
 * Retire le préfixe de langue d'un pathname.
 * Ex : "/de/blog/post-x" → "/blog/post-x"
 */
export function stripLangFromPath(pathname) {
  const lang = extractLangFromPath(pathname);
  if (!lang) return pathname || '/';
  const stripped = pathname.replace(/^\/[a-z]{2}/i, '') || '/';
  return stripped;
}

/**
 * Construit un pathname avec préfixe de langue.
 * Ex : ("/blog/post-x", "de") → "/de/blog/post-x"
 *      ("/blog/post-x", "fr") → "/blog/post-x" (FR = défaut, pas de préfixe)
 */
export function buildLocalizedPath(pathname, lang) {
  const cleanPath = stripLangFromPath(pathname);
  if (lang === DEFAULT_LANGUAGE) return cleanPath;
  if (cleanPath === '/') return `/${lang}`;
  return `/${lang}${cleanPath}`;
}

/**
 * Construit l'URL absolue canonique pour une langue donnée.
 * Utilisé pour les balises `<link rel="alternate" hreflang>`.
 */
export function buildAlternateUrl(pathname, lang) {
  return `${PUBLIC_BASE_URL}${buildLocalizedPath(pathname, lang)}`;
}

/**
 * Génère la liste des balises hreflang pour une page.
 * Retourne un tableau d'objets { hreflang, href }.
 */
export function generateHreflangs(pathname) {
  const cleanPath = stripLangFromPath(pathname);
  return [
    ...SUPPORTED_LANGUAGES.map((lang) => ({
      hreflang: lang,
      href: buildAlternateUrl(cleanPath, lang),
    })),
    {
      hreflang: 'x-default',
      href: buildAlternateUrl(cleanPath, DEFAULT_LANGUAGE),
    },
  ];
}
